import { LitElement, html, property } from 'lit-element'
import tippy from 'tippy.js'
import { bemBlock } from '@polleverywhere/bem-helpers'
import { defineElementSlot } from '@polleverywhere/element-helpers'
import { register } from '@polleverywhere/hmr-custom-element'
import TooltipRegistry from './registry'
import * as tippyProps from './tippy-props'
import './tooltip.scss'
import '@polleverywhere/pe-button'

/**
 * List of valid placement preferences based on
 * https://atomiks.github.io/tippyjs/v6/all-props/#placement.
 * @type {Array}
 */
export const placements = [
  'top',
  'top-start',
  'top-end',
  'right',
  'right-start',
  'right-end',
  'bottom',
  'bottom-start',
  'bottom-end',
  'left',
  'left-start',
  'left-end',
  'auto',
  'auto-start',
  'auto-end'
]

/**
 * List of valid tooltip themes.
 * @type {Array}
 */
export const themes = [
  'info',
  'onboarding',
  'tooltip'
]

/**
 * Allows consumers to declaratively initialize a tooltip for an element and
 * declaratively set the tooltip's content using standard HTML. Behind the
 * scenes, this element relies on [Tippy.js](https://atomiks.github.io/tippyjs/)
 * to render and position the tooltip next to a reference element.
 *
 * The reference element is by default the parent element of where this element
 * is added to. For example, here's adding a tooltip for the following controls.
 *
 * ```
 * <button
 *   <pe-tooltip>Save this form</pe-tooltip>
 *   Save
 * </button>
 *
 * <a href='/activities' aria-label='Go to activities'>
 *   <pe-tooltip>Go to activities</pe-tooltip>
 *   Activities
 * </a>
 * ```
 *
 * @element pe-tooltip
 *
 * @slot - Tooltip content with any valid HTML.
 *
 * @prop {boolean} arrow - Tippy.js
 *  [arrow](https://atomiks.github.io/tippyjs/v6/all-props/#arrow) property.
 * @prop {string} confirmation - When confirmation is provided, the tooltip
 *  renders a confirmation CTA labeled by this value that users must interact
 *  with to dismiss it.
 * @prop {Number} delayShow - Number of milliseconds to wait before showing the
 *  tooltip. See Tippy.js
 *  [delay](https://atomiks.github.io/tippyjs/v6/all-props/#delay) property.
 * @prop {Number} delayHide - Number of milliseconds to wait before hiding the
 *  tooltip. See Tippy.js
 *  [delay](https://atomiks.github.io/tippyjs/v6/all-props/#delay) property.
 * @prop {boolean} disabled - Disables this tooltip from showing by destroying
 *  the tippy instance under the hood. Consumers must use this to conditionally
 *  enable or disable the tooltip rather than dynamically adding or removing the
 *  element in the template.
 * @prop {string} group - When group is assigned, all tooltips belonging to the
 *  same group are displayed using a singleton Tippy instance. This lets each
 *  tooltip share a common Tippy instance so when user's hover over different
 *  items in the group, the tooltip remains visible and doesn't require a new
 *  trigger event to occur. See
 *  [singleton demo](https://atomiks.github.io/tippyjs/#singleton).
 * @prop {Number} offsetDistance - Displaces the tooltip away from its parent
 *  reference element in pixels. See Tippy.js
 *  [offset](https://atomiks.github.io/tippyjs/v6/all-props/#offset) property.
 * @prop {Number} offsetSkidding - Displaces the tooltip along and adjacent to
 *  its parent reference element in pixels. See Tippy.js
 *  [offset](https://atomiks.github.io/tippyjs/v6/all-props/#offset) property.
 * @prop {Number} zIndex - Tooltip zIndex. See Tippy.js
 *  [offset](https://atomiks.github.io/tippyjs/v6/all-props/#zIndex) property.
 * @prop {string} placement - Tippy.js
 *  [placement](https://atomiks.github.io/tippyjs/v6/all-props/#placement)
 *  property.
 * @prop {boolean} show - Explicitly configure the tooltip to show immediately
 *  after it's been initialized. Does not work for "grouped" tooltips using a
 *  singleton instance. Use this if you need to show the tooltip on first load.
 * @prop {string} theme - Sets the tooltip theme to use. Each theme typically
 *  results in a different visual theme and potentially different behavior.
 *  Valid themes are `[tooltip,info,onboarding]`.
 *
 * @fires {Object} initialized - Fires when tooltip tippy instance is created.
 *  Consumers can listen for this event to then call methods against the tippy
 *  instance or store it for later referencing.
 * @fires {Object} confirmed - Fires when the confirmation CTA is acted upon.
 *  This is only applicable if `confirmation` property is set.
 */
export default class Tooltip extends LitElement {
  static get tagName () { return 'pe-tooltip' }

  @property({ type: Boolean })
  arrow

  @property({ type: String })
  confirmation

  @property({ type: Number, attribute: 'delay-show' })
  delayShow

  @property({ type: Number, attribute: 'delay-hide' })
  delayHide

  @property({ type: Boolean })
  disabled

  @property({ type: String })
  group

  @property({ type: Number, attribute: 'offset-skidding' })
  offsetSkidding

  @property({ type: Number, attribute: 'offset-distance' })
  offsetDistance

  @property({ type: Number, attribute: 'z-index' })
  zIndex

  @property({ type: String })
  placement

  @property({ type: Boolean })
  show

  @property({ type: String })
  theme = 'tooltip'

  /**
   * Holds reference to the
   * [Tippy.js](https://atomiks.github.io/tippyjs/v6/tippy-instance/) instance.
   * @type {Tippy}
   */
  tippy

  // Private vars
  _reference
  _registry

  connectedCallback () {
    super.connectedCallback()
    defineElementSlot(this)
  }

  createRenderRoot () { return this }

  firstUpdated () {
    this._registry = TooltipRegistry.getInstance()
    this._targetElement = this.parentElement

    // Create tippy instance with this element as the content.
    updateTippy.call(this)

    // If the target element is removed, attempt to detect that and react by
    // destroying the tippy instance and removing it from the registry.
    this.observer = new MutationObserver((mutations) => {
      const removedNodes = mutations.reduce((accum, mutation) => [
        ...accum,
        ...mutation.removedNodes
      ], [])

      if (removedNodes.includes(this._targetElement)) {
        this.tippy.destroy()
      }
    })
    this.observer.observe(this._targetElement.parentElement, {
      childList: true
    })
  }

  updated (changedMap) {
    // When tooltip is disabled, destroy the tippy instance and prevent it from
    // being recreated on other property updates.
    if (this.disabled) {
      this.tippy?.destroy()
      return
    }

    updateTippy.call(this)

    if (this.show) {
      this.tippy.show()
    } else {
      this.tippy.hide()
    }
  }

  render () {
    if (!themes.includes(this.theme)) {
      throw new Error(`Theme "${this.theme}" is invalid and must be one of:\n[${themes.join(',')}]`)
    }

    if (this.placement && !placements.includes(this.placement)) {
      throw new Error(`Placement "${this.placement}" is invalid and must be one of:\n[${placements.join(',')}]`)
    }

    const mods = Object.fromEntries(themes.map(theme =>
      [theme, theme === this.theme]))

    return html`
      <div class=${bem(mods)}>
        <div class=${bem('content')}>
          ${this.slots()}
        </div>
        ${renderControls.call(this)}
      </div>
    `
  }
}

function updateTippy () {
  const defaultProps = { ...tippyProps[this.theme] }
  const props = {
    ...defaultProps,
    arrow: this.arrow || defaultProps.arrow,
    delay: [
      Number.isInteger(this.delayShow)
        ? this.delayShow
        : defaultProps.delay[0],
      Number.isInteger(this.delayShow)
        ? this.delayHide
        : defaultProps.delay[1]
    ],
    hideOnClick: !this.confirmation,
    interactive: !!this.confirmation,
    offset: [
      Number.isInteger(this.offsetSkidding)
        ? this.offsetSkidding
        : defaultProps.offset[0],
      Number.isInteger(this.offsetDistance)
        ? this.offsetDistance
        : defaultProps.offset[1]
    ],
    placement: this.placement || defaultProps.placement,
    trigger: this.confirmation ? 'click' : defaultProps.trigger,
    zIndex: this.zIndex
  }

  if (this.tippy) {
    this.tippy.setProps({
      ...props,
      onAfterUpdate: () => {}
    })
  } else {
    this.tippy = tippy(this._targetElement, {
      ...props,
      content: this,
      onCreate: (instance) => {
        this.id = [Tooltip.tagName, instance.id].join('-')
      },
      onDestroy: (instance) => {
        this._registry.unregister(this)
        this.tippy = undefined
      }
    })

    // Must register this element after its tippy instance is created.
    this._registry.register(this)

    // Use this to dispatch an init event for consumers to listen to.
    this.tippy.setProps({
      onAfterUpdate: (instance) => {
        this.dispatchEvent(new Event('initialized'))
      }
    })
  }
}

function handleConfirmation (e) {
  this.tippy.destroy()
  this.dispatchEvent(new Event('confirmed'))
}

function renderControls () {
  if (!this.confirmation) return

  return html`
    <div class=${bem('controls')}>
      <pe-button
        @click=${handleConfirmation}
        class=${bem('confirmation')}
        label=${this.confirmation}
        size=${this.theme === 'tooltip' ? 'compact' : 'cozy'}
        type='link'
      ></pe-button>
    </div>
  `
}

const bem = bemBlock(Tooltip.tagName)
register(module, Tooltip.tagName, Tooltip)
